
import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useBabies } from '../../hooks/useBabies';
import TopNav from '../../components/feature/TopNav';
import BottomNav from '../../components/feature/BottomNav';
import AvisoAcceso from '../../components/feature/AvisoAcceso';
import IndicadorPendientes from '../../components/feature/IndicadorPendientes';
import TodaySummary from './components/TodaySummary';
import QuickActions from './components/QuickActions';
import ActivityTimeline from './components/ActivityTimeline';
import AddBabyModal from './components/AddBabyModal';
import AddActivityModal from './components/AddActivityModal';

export default function DashboardPage() {
  const { user, loading } = useAuth();
  const { activeBaby } = useBabies();
  const [showAddBabyModal, setShowAddBabyModal] = useState(false);
  const [showAddActivityModal, setShowAddActivityModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="bg-white rounded-2xl p-8 shadow-xl">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-600 mt-4 text-center">Cargando dashboard...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  const handleDateChange = (direction: 'prev' | 'next' | 'today') => {
    const newDate = new Date(selectedDate);
    
    if (direction === 'prev') {
      newDate.setDate(newDate.getDate() - 1);
    } else if (direction === 'next') {
      newDate.setDate(newDate.getDate() + 1);
    } else if (direction === 'today') {
      return setSelectedDate(new Date());
    }
    
    setSelectedDate(newDate);
  };

  const formatSelectedDate = () => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (selectedDate.toDateString() === today.toDateString()) {
      return 'Hoy';
    } else if (selectedDate.toDateString() === yesterday.toDateString()) {
      return 'Ayer';
    } else if (selectedDate.toDateString() === tomorrow.toDateString()) {
      return 'Mañana';
    } else {
      return selectedDate.toLocaleDateString('es-ES', { 
        weekday: 'long', 
        day: 'numeric', 
        month: 'long' 
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <TopNav
        title={activeBaby ? `Dashboard - ${activeBaby.name}` : 'Dashboard'}
        showBabySelector={true}
        showAdminButton={true}
      />

      {/* Aviso de acceso por vencer. Va arriba de todo y solo aparece
          en los últimos días, para que le dé tiempo de buscar el
          código antes de quedarse afuera. */}
      <div className="pt-24">
        <IndicadorPendientes />
        <AvisoAcceso />
      </div>

      <div className="pb-20 px-4">
        {!activeBaby ? (
          <div className="text-center py-12">
            <i className="ri-baby-line text-6xl text-gray-300 mb-4"></i>
            <h2 className="text-xl font-semibold text-gray-600 mb-2">
              No hay bebés registrados
            </h2>
            <p className="text-gray-500 mb-6">
              Agrega tu primer bebé para empezar a anotar
            </p>
            <button
              onClick={() => setShowAddBabyModal(true)}
              className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-6 py-3 rounded-xl font-medium hover:from-blue-600 hover:to-indigo-700 transition-colors"
            >
              <i className="ri-add-line mr-2"></i>
              Agregar mi primer bebé
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            <TodaySummary />
            <QuickActions />
            
            {/* Selector de Fecha */}
            <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between">
                <button
                  onClick={() => handleDateChange('prev')}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <i className="ri-arrow-left-line text-xl text-gray-600"></i>
                </button>
                
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => handleDateChange('today')}
                    className="px-4 py-2 bg-teal-100 text-teal-700 rounded-lg text-sm font-medium hover:bg-teal-200 transition-colors"
                  >
                    Hoy
                  </button>
                  <h3 className="text-lg font-semibold text-gray-800 capitalize">
                    {formatSelectedDate()}
                  </h3>
                </div>
                
                <button
                  onClick={() => handleDateChange('next')}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <i className="ri-arrow-right-line text-xl text-gray-600"></i>
                </button>
              </div>
            </div>
            
            <ActivityTimeline date={selectedDate} />
          </div>
        )}
      </div>

      <BottomNav />

      {showAddBabyModal && (
        <AddBabyModal onClose={() => setShowAddBabyModal(false)} />
      )}

      {showAddActivityModal && (
        <AddActivityModal onClose={() => setShowAddActivityModal(false)} />
      )}
    </div>
  );
}
